import React from "react";
import { Link } from "react-router-dom";
import renderBBCode from "@/components/bbcode";

/**
 * Render a forum post / thread body:
 *   1) Convert BBCode tags to React nodes (bold, italic, quote, url, img, …)
 *   2) Then walk plain-text segments to highlight @mentions as clickable Links.
 *
 * `mentions` is the array stored on the doc: [{user_id, name, picture}].
 */
const MENTION_RE = /(?:^|(?<=\s))@([a-zA-Z0-9_.\-]{2,30})/g;

function linkifyMentions(text, byName) {
  if (!text || typeof text !== "string") return text;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    const handle = m[1];
    const matchStart = m.index + (m[0].startsWith("@") ? 0 : 1);
    if (matchStart > last) {
      out.push(<React.Fragment key={`t${key++}`}>{text.slice(last, matchStart)}</React.Fragment>);
    }
    const u = byName[handle.toLowerCase()];
    if (u) {
      out.push(
        <Link key={`m${key++}`} to={`/u/${u.user_id}`}
          className="text-[var(--tt-primary)] font-semibold hover:underline">@{u.name}</Link>
      );
    } else {
      out.push(<span key={`m${key++}`} className="text-[var(--tt-primary)]/70 font-semibold">@{handle}</span>);
    }
    last = matchStart + 1 + handle.length;
  }
  if (last < text.length) {
    out.push(<React.Fragment key={`t${key++}`}>{text.slice(last)}</React.Fragment>);
  }
  return out;
}

/** Walk the rendered BBCode tree and inject mention links into plain-text children. */
function injectMentions(node, byName, depth = 0) {
  if (typeof node === "string") return linkifyMentions(node, byName);
  if (!React.isValidElement(node)) return node;
  // Don't touch the inside of <a>, <code>, <pre>
  if (["a", "code", "pre"].includes(node.type)) return node;
  const newChildren = React.Children.map(node.props.children, (c, i) =>
    <React.Fragment key={i}>{injectMentions(c, byName, depth + 1)}</React.Fragment>
  );
  return React.cloneElement(node, { ...node.props, key: node.key }, newChildren);
}

export default function ForumBody({ text, mentions }) {
  const byName = {};
  for (const m of mentions || []) {
    if (m && m.name) byName[m.name.toLowerCase()] = m;
  }
  const rendered = renderBBCode(text);
  if (!rendered) return null;
  return (
    <div className="whitespace-pre-wrap break-words">
      {React.Children.map(rendered, (c, i) => (
        <React.Fragment key={i}>{injectMentions(c, byName)}</React.Fragment>
      ))}
    </div>
  );
}
